import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertNotification, Col, LabeledCheckbox, LabeledRadio, Link, Row, Tooltip } from '@commonsku/styles';
import { useDispatch } from 'react-redux';
import { useHasCapabilities, useIdentity } from '../hooks';
import DepositInvoice from './DepositInvoice';
import updateOrder from '../actions/order';
import OrderCreditCardFeeEditHandle from './OrderCreditCardFeeEditHandle';
import { EditProject } from '../types';
import { EditOrder } from '../types/order';

interface SalesOrderUpfrontPaymentSettingsProps {
    project: EditProject;
    order: EditOrder;
    credit_card_payable: string;
    stripe_cc_payable: any;
    stripe_ach_payable: any;
    deposit_invoice?: any;
    onUpdateDepositInvoice: any;
    onDeleteDepositInvoice: any;
    onAddDepositInvoice: any;
}

const SalesOrderUpfrontPaymentSettings = ({ deposit_invoice, project, order, credit_card_payable, stripe_cc_payable, stripe_ach_payable, onUpdateDepositInvoice, onAddDepositInvoice, onDeleteDepositInvoice }: SalesOrderUpfrontPaymentSettingsProps) => {
    const dispatch = useDispatch();
    const identity = useIdentity();
    const canGenerateInvoice = useHasCapabilities(['MODIFY-INVOICE']);

    const defaultValues = useMemo(() => ({
        upfront_payment: 'NONE', // NONE, INVOICE, DEPOSIT
        allow_cc_payment: 0,
        allow_ach_payment: 0,
        payment_cc_fee_enabled: 0,
        payment_cc_fee_percentage: 3.0,
    }), []);

    const [form, setForm] = useState(defaultValues);

    const saveOrder = useCallback((field, value) => {
        dispatch(updateOrder(order.order_id, field, form[field], value));
    }, [dispatch, form, order.order_id]);

    useEffect(() => {
        let values = defaultValues;
        values = {
            upfront_payment: order.upfront_payment ?? 'NONE',
            allow_cc_payment: order.allow_cc_payment ?? 0,
            allow_ach_payment: order.allow_ach_payment ?? 0,
            payment_cc_fee_enabled: order.payment_cc_fee_enabled ?? 0,
            payment_cc_fee_percentage: order.payment_cc_fee_percentage ?? 3.0,
        };

        // Backwards compatiblity
        if (deposit_invoice && values.upfront_payment === 'NONE') {
            values.upfront_payment = 'DEPOSIT';
        }

        setForm(values);
    }, [defaultValues, deposit_invoice, order.allow_ach_payment, order.allow_cc_payment, order.payment_cc_fee_enabled, order.payment_cc_fee_percentage, order.upfront_payment]);

    const handleUpfrontPaymentOption = useCallback((value) => {
        if (value === 'INVOICE') {
            saveOrder('upfront_payment', 'INVOICE');
            if (deposit_invoice) {
                onDeleteDepositInvoice(deposit_invoice.deposit_id, order.order_id);
            }
        }

        if (value === 'DEPOSIT') {
            saveOrder('upfront_payment', 'DEPOSIT');
            onAddDepositInvoice(order.job_id, order.order_id);
        }

        if (value === 'NONE') {
            saveOrder('upfront_payment', 'NONE');
            if (deposit_invoice) {
                onDeleteDepositInvoice(deposit_invoice.deposit_id, order.order_id);
            }
        }

    }, [deposit_invoice, onAddDepositInvoice, onDeleteDepositInvoice, order.job_id, order.order_id, saveOrder]);

    const locked = 1 == order.locked;
    const depositPaid = deposit_invoice && deposit_invoice.deposit_amount_received > 0;
    const upfrontPaymentEnabled = form['upfront_payment'] === 'INVOICE' || form['upfront_payment'] === 'DEPOSIT';
    const canEnableInvoicePayment = canGenerateInvoice && (credit_card_payable === 'CARDCONNECT' || credit_card_payable === 'STRIPE');
    const fullPaymentDisabledTooltip = canGenerateInvoice ?
        <span>You must have an integration with a payment system like Stripe or Clover Connect enabled to require full upfront payment.</span> :
        <span>You don't have the permissions required to generate an invoice.
            <Link as={'a'} href="https://help.commonsku.com/knowledge/approve-pay" target='_blank' rel="noreferrer">&nbsp;Learn More</Link>
        </span>;

    return <>
        <Tooltip delayHide={3000} clickable id="commentTip" place="left" style={{ zIndex: '100', width: '350px', textAlign: 'left', padding: '1rem', background: '#123952' }}>
            {fullPaymentDisabledTooltip}
        </Tooltip>
        <Row style={{ textAlign: 'left' }}>
            <Col padded sm={12}>
                <LabeledCheckbox disabled={locked || depositPaid} label='Require Upfront Payment' checked={upfrontPaymentEnabled} onClick={(e) => {
                    if (locked) return;
                    if (upfrontPaymentEnabled) {
                        handleUpfrontPaymentOption('NONE');
                    } else {
                        if (canEnableInvoicePayment) {
                            handleUpfrontPaymentOption('INVOICE');
                        } else {
                            handleUpfrontPaymentOption('DEPOSIT');
                        }

                    }

                }}
                />
            </Col>
            {form['upfront_payment'] !== 'NONE' ?
                <Col padded sm={12}>
                    <span data-tooltip-id={!canEnableInvoicePayment ? 'commentTip' : null}>
                        <LabeledRadio
                            name='upfront_payment_radio' // This is import other wise on change will fire twice..
                            disabled={!canEnableInvoicePayment || locked || depositPaid}
                            label='Full'
                            checked={form['upfront_payment'] === 'INVOICE'}
                            onChange={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleUpfrontPaymentOption('INVOICE');
                            }}
                        />
                    </span>
                    <LabeledRadio
                        name='upfront_payment_radio' // This is import other wise on change will fire twice..
                        label='Deposit'
                        checked={form['upfront_payment'] === 'DEPOSIT'}
                        disabled={locked || depositPaid}
                        onChange={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleUpfrontPaymentOption('DEPOSIT');
                        }}
                    />
                </Col> : null}
            {form['upfront_payment'] === 'INVOICE' && order.invoice_paid ?
                <Col padded sm={12}><AlertNotification alertType='success'>Payment Received</AlertNotification></Col>
                : null}
            {form['upfront_payment'] === 'INVOICE' && !order.invoice_paid ?
                <Col padded sm={12}><AlertNotification alertType='warn'>Payment Not Received</AlertNotification></Col>
                : null}
            {form['upfront_payment'] === 'INVOICE' && !order.invoice_paid ?
                <Col padded sm={12}>
                    {(credit_card_payable === 'CARDCONNECT' || (credit_card_payable === 'STRIPE' && stripe_cc_payable)) &&
                        <>
                            <LabeledCheckbox
                                disabled={((!order.allow_ach_payment || !stripe_ach_payable) && order.allow_cc_payment) || locked}
                                checked={order.allow_cc_payment}
                                label="Allow Credit Card"
                                value={order.allow_cc_payment}
                                onChange={() => {
                                    saveOrder('allow_cc_payment', +order.allow_cc_payment === 1 ? 0 : 1);
                                }}
                            />
                            <OrderCreditCardFeeEditHandle order={order} project={project} isUpfrontPayment={true} />
                        </>
                    }
                </Col> : null}
            {form['upfront_payment'] === 'INVOICE' && !order.invoice_paid ?
                <Col padded sm={12}>
                    {credit_card_payable === 'STRIPE' && stripe_ach_payable &&
                        <LabeledCheckbox
                            disabled={((!order.allow_cc_payment || !stripe_cc_payable) && order.allow_ach_payment) || locked}
                            label="Allow ACH"
                            checked={order.allow_ach_payment}
                            value={order.allow_ach_payment}
                            onChange={() => {
                                saveOrder('allow_ach_payment', +order.allow_ach_payment === 1 ? 0 : 1);
                            }}
                        />
                    }
                </Col> : null}
            {form['upfront_payment'] === 'DEPOSIT'
                && deposit_invoice
                && parseFloat(deposit_invoice.deposit_amount) > 0
                && parseFloat(deposit_invoice.deposit_amount_received) >= parseFloat(deposit_invoice.deposit_amount
                ) ?
                <Col padded sm={12}><AlertNotification alertType='success'>Deposit Received</AlertNotification></Col>
                : null}
            {form['upfront_payment'] === 'DEPOSIT'
                && (!deposit_invoice
                    || parseFloat(deposit_invoice.deposit_amount) === 0
                    || parseFloat(deposit_invoice.deposit_amount_received) < parseFloat(deposit_invoice.deposit_amount)
                ) ?
                <Col padded sm={12}><AlertNotification alertType='warn'>Deposit Not Received</AlertNotification></Col>
                : null}
            {form['upfront_payment'] === 'DEPOSIT' && deposit_invoice ?
                <DepositInvoice
                    hideStatus={true}
                    deposit_invoice={deposit_invoice}
                    onUpdateDepositInvoice={onUpdateDepositInvoice}
                    locked={locked} identity={identity}
                    stripe_cc_payable={stripe_cc_payable}
                    stripe_ach_payable={stripe_ach_payable}
                    credit_card_payable={credit_card_payable} />
                : null}
        </Row>
    </>;
};

export default SalesOrderUpfrontPaymentSettings;